<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import FormsAPI from 'custom/dashboard/api/forms';
import PipelinesAPI from 'custom/dashboard/api/pipelines';
import CardTaskTypesAPI from 'custom/dashboard/api/cardTaskTypes';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import BusinessDay from 'dashboard/routes/dashboard/settings/inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BreakTime from './BreakTime.vue';
import { openWindowFor, isValidBreak } from './breakTimeHelper';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
// ComboBox needs a truthy value for every option.
const NONE = 'none';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const form = ref(null);
const pipelines = ref([]);
const stages = ref([]);
const taskTypes = ref([]);
const isSaving = ref(false);

const name = ref('');
const description = ref('');
const successMessage = ref('');
const isActive = ref(true);
const inboxId = ref(NONE);
const createsCard = ref(false);
const pipelineId = ref('');
const stageId = ref(NONE);
const taskTypeId = ref('');
const timeSlots = ref([]);
// Each day's break (e.g. lunch), keyed by day_of_week, as `{ start, end }` in
// minutes or null. Kept apart from the slots: those are BusinessDay's shape,
// shared with the inbox business hours, which have no break.
const breaks = ref({});
// Whether a visit form's acknowledgement also goes out after the 24h window, as
// the system template (see Forms::AcknowledgementService).
const acknowledgementTemplateEnabled = ref(false);
// Where the acknowledgement template stands: off, on and in use, or on and still
// awaiting Meta's approval in some inbox. Follows the switch at once; the server
// only says whether a saved "on" is already in use.
const ACKNOWLEDGEMENT_STATUS_STYLES = {
  active: 'bg-n-teal-5 text-n-teal-11',
  pending: 'bg-n-amber-5 text-n-amber-11',
  disabled: 'bg-n-slate-4 text-n-slate-11',
};
const acknowledgementStatus = computed(() => {
  if (!acknowledgementTemplateEnabled.value) return 'disabled';
  return form.value?.acknowledgement_template_status === 'active'
    ? 'active'
    : 'pending';
});
const acknowledgementStatusLabel = computed(
  () =>
    ({
      active: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.STATUS.active'),
      pending: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.STATUS.pending'),
      disabled: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.STATUS.disabled'),
    })[acknowledgementStatus.value]
);
// The inboxes still holding it back, with where each one stands.
const pendingInboxes = computed(() => {
  const labels = {
    pending: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.INBOX_STATUS.pending'),
    rejected: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.INBOX_STATUS.rejected'),
    missing: t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.INBOX_STATUS.missing'),
  };
  return (form.value?.acknowledgement_template_inboxes || [])
    .filter(inbox => inbox.status !== 'approved')
    .map(inbox => ({ ...inbox, label: labels[inbox.status] }));
});

// The visit forms are the ones whose system key the backend lists in Form::VISIT_KEYS.
const isVisit = computed(() => !!form.value?.system_key?.startsWith('visit_'));

const inboxOptions = computed(() => [
  { value: NONE, label: t('CARDS.FORMS.NO_INBOX') },
  ...inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);
const pipelineOptions = computed(() =>
  pipelines.value
    .filter(
      pipeline => pipeline.status === 'active' && pipeline.item_type === 'Card'
    )
    .map(pipeline => ({ value: pipeline.id, label: pipeline.name }))
);
const stageOptions = computed(() => [
  { value: NONE, label: t('CARDS.FORMS.ENTRY_STAGE') },
  ...stages.value.map(stage => ({ value: stage.id, label: stage.name })),
]);
const taskTypeOptions = computed(() =>
  taskTypes.value.map(type => ({ value: type.id, label: type.name }))
);

const hasInvalidSlot = computed(() =>
  timeSlots.value.some(
    slot =>
      slot.from &&
      (!slot.valid ||
        !isValidBreak(breaks.value[slot.day], openWindowFor(slot)))
  )
);
const canSave = computed(
  () =>
    !!name.value.trim() &&
    (!createsCard.value || (!!pipelineId.value && !!taskTypeId.value)) &&
    !hasInvalidSlot.value &&
    !isSaving.value
);

const loadStages = async id => {
  stages.value = id ? (await PipelinesAPI.getStages(id)).data : [];
};

const fill = record => {
  form.value = record;
  name.value = record.name;
  description.value = record.description || '';
  successMessage.value = record.success_message || '';
  isActive.value = record.status === 'active';
  inboxId.value = record.inbox_id || NONE;
  createsCard.value = !!record.pipeline_id;
  pipelineId.value = record.pipeline_id || '';
  stageId.value = record.stage_id || NONE;
  taskTypeId.value = record.card_task_type_id || '';
  timeSlots.value = timeSlotParse(record.working_hours || []);
  breaks.value = Object.fromEntries(
    (record.working_hours || []).map(day => [
      day.day_of_week,
      day.zimobi_break_start_minute == null
        ? null
        : {
            start: day.zimobi_break_start_minute,
            end: day.zimobi_break_end_minute,
          },
    ])
  );
  acknowledgementTemplateEnabled.value =
    record.acknowledgement_template_enabled;
};

const onPipelineChange = async id => {
  pipelineId.value = id;
  stageId.value = NONE;
  await loadStages(id);
};

watch(createsCard, on => {
  if (on) return;
  pipelineId.value = '';
  stageId.value = NONE;
  taskTypeId.value = '';
  stages.value = [];
});

const onSlotUpdate = (day, slot) => {
  timeSlots.value = timeSlots.value.map(item =>
    item.day === day ? slot : item
  );
};

// Only a break that fits its day comes off the day's total; an invalid one is
// already flagged under the day.
const breakMinutesOf = day => {
  const pause = breaks.value[day];
  const slot = timeSlots.value.find(item => item.day === day);
  if (!pause || !isValidBreak(pause, openWindowFor(slot))) return 0;
  return pause.end - pause.start;
};

const onBreakUpdate = (day, value) => {
  breaks.value = { ...breaks.value, [day]: value };
};

// A closed day keeps no break, whatever was last picked for it.
const workingHoursPayload = () =>
  timeSlotTransform(timeSlots.value).map(day => {
    const pause = day.closed_all_day ? null : breaks.value[day.day_of_week];
    return {
      ...day,
      zimobi_break_start_minute: pause?.start ?? null,
      zimobi_break_end_minute: pause?.end ?? null,
    };
  });

const payload = () => ({
  name: name.value.trim(),
  description: description.value,
  success_message: successMessage.value,
  status: isActive.value ? 'active' : 'disabled',
  inbox_id: inboxId.value === NONE ? null : inboxId.value,
  pipeline_id: pipelineId.value || null,
  stage_id: stageId.value === NONE ? null : stageId.value,
  card_task_type_id: taskTypeId.value || null,
  ...(isVisit.value && {
    acknowledgement_template_enabled: acknowledgementTemplateEnabled.value,
  }),
  ...(form.value.restricts_to_working_hours && {
    working_hours: workingHoursPayload(),
  }),
});

const save = async () => {
  isSaving.value = true;
  try {
    await FormsAPI.update(form.value.id, { form: payload() });
    useAlert(t('CARDS.FORMS.SAVED'));
    router.push({ name: 'forms_index', params: route.params });
  } catch {
    useAlert(t('CARDS.FORMS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  try {
    const [formResponse, pipelinesResponse, typesResponse] = await Promise.all([
      FormsAPI.show(route.params.formId),
      PipelinesAPI.get(),
      CardTaskTypesAPI.get(),
    ]);
    pipelines.value = pipelinesResponse.data;
    taskTypes.value = typesResponse.data;
    fill(formResponse.data);
    await loadStages(form.value.pipeline_id);
  } catch {
    useAlert(t('CARDS.FORMS.LOAD_ERROR'));
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="!form"
    :loading-message="$t('CARDS.FORMS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="form?.name || $t('CARDS.FORMS.EDIT')"
        :description="form?.public_url || ''"
        :back-button-label="$t('CARDS.FORMS.TITLE')"
      />
    </template>

    <template #body>
      <form
        v-if="form"
        class="flex flex-col max-w-2xl gap-8"
        data-testid="form-edit-page"
        @submit.prevent="save"
      >
        <section class="flex flex-col gap-4">
          <Input
            v-model="name"
            :label="$t('CARDS.FORMS.NAME')"
            data-testid="form-name"
          />
          <TextArea
            v-model="description"
            :label="$t('CARDS.FORMS.DESCRIPTION')"
            data-testid="form-description"
          />
          <TextArea
            v-model="successMessage"
            :label="$t('CARDS.FORMS.SUCCESS_MESSAGE')"
            data-testid="form-success-message"
          />
          <label class="flex items-center justify-between gap-3">
            <span class="text-sm text-n-slate-12">
              {{ $t('CARDS.FORMS.ACTIVE') }}
            </span>
            <Switch v-model="isActive" data-testid="form-active" />
          </label>
          <div class="flex flex-col gap-1" data-testid="form-inbox">
            <span class="text-sm text-n-slate-12">
              {{ $t('CARDS.FORMS.INBOX') }}
            </span>
            <ComboBox v-model="inboxId" :options="inboxOptions" />
            <span class="text-xs text-n-slate-11">
              {{ $t('CARDS.FORMS.INBOX_HINT') }}
            </span>
          </div>
        </section>

        <section
          class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak"
        >
          <label class="flex items-center justify-between gap-3">
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('CARDS.FORMS.CREATE_CARD') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  createsCard
                    ? $t('CARDS.FORMS.CREATE_CARD_ON')
                    : $t('CARDS.FORMS.CREATE_CARD_OFF')
                }}
              </span>
            </span>
            <Switch v-model="createsCard" data-testid="form-creates-card" />
          </label>
          <template v-if="createsCard">
            <div class="flex flex-col gap-1" data-testid="form-pipeline">
              <span class="text-sm text-n-slate-12">
                {{ $t('CARDS.FORMS.PIPELINE') }}
              </span>
              <ComboBox
                :model-value="pipelineId"
                :options="pipelineOptions"
                :placeholder="$t('CARDS.FORMS.PIPELINE')"
                @update:model-value="onPipelineChange"
              />
            </div>
            <div class="flex flex-col gap-1" data-testid="form-stage">
              <span class="text-sm text-n-slate-12">
                {{ $t('CARDS.FORMS.STAGE') }}
              </span>
              <ComboBox
                v-model="stageId"
                :options="stageOptions"
                :disabled="!pipelineId"
              />
            </div>
            <div class="flex flex-col gap-1" data-testid="form-task-type">
              <span class="text-sm text-n-slate-12">
                {{ $t('CARDS.FORMS.TASK_TYPE') }}
              </span>
              <ComboBox
                v-model="taskTypeId"
                :options="taskTypeOptions"
                :placeholder="$t('CARDS.FORMS.TASK_TYPE')"
              />
            </div>
          </template>
        </section>

        <section
          v-if="form.restricts_to_working_hours"
          class="flex flex-col gap-2"
          data-testid="form-hours"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CARDS.FORMS.HOURS') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('CARDS.FORMS.HOURS_HINT') }}
          </span>
          <!-- BusinessDay is a table row since Chatwoot 4.11, as in WeeklyAvailability. -->
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <tbody class="divide-y divide-n-weak">
              <template v-for="slot in timeSlots" :key="slot.day">
                <BusinessDay
                  :day-name="DAY_NAMES[slot.day]"
                  :time-slot="slot"
                  :break-minutes="breakMinutesOf(slot.day)"
                  @update="data => onSlotUpdate(slot.day, data)"
                />
                <tr v-if="openWindowFor(slot)">
                  <td colspan="3" class="p-0">
                    <BreakTime
                      :model-value="breaks[slot.day]"
                      :open-window="openWindowFor(slot)"
                      @update:model-value="
                        value => onBreakUpdate(slot.day, value)
                      "
                    />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </section>

        <section
          v-if="isVisit"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
          data-testid="form-ack-template"
        >
          <label class="flex items-center justify-between gap-3">
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.TITLE') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.TOGGLE') }}
              </span>
            </span>
            <Switch
              v-model="acknowledgementTemplateEnabled"
              data-testid="form-ack-template-enabled"
            />
          </label>
          <div class="flex flex-col gap-2">
            <span
              class="self-start h-6 px-2 text-xs font-medium leading-6 rounded-md"
              :class="ACKNOWLEDGEMENT_STATUS_STYLES[acknowledgementStatus]"
              data-testid="form-ack-template-status"
            >
              {{ acknowledgementStatusLabel }}
            </span>
            <template v-if="acknowledgementStatus === 'pending'">
              <ul
                v-if="pendingInboxes.length"
                class="flex flex-col gap-1 mb-0 list-none ltr:pl-0 rtl:pr-0"
                data-testid="form-ack-template-inboxes"
              >
                <li
                  v-for="inbox in pendingInboxes"
                  :key="inbox.name"
                  class="flex items-center justify-between gap-3 text-xs"
                >
                  <span class="text-n-slate-12">{{ inbox.name }}</span>
                  <span
                    :class="
                      inbox.status === 'rejected'
                        ? 'text-n-ruby-11'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ inbox.label }}
                  </span>
                </li>
              </ul>
              <span class="text-xs text-n-slate-11">
                {{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.PENDING_HINT') }}
              </span>
            </template>
          </div>
          <ul
            class="flex flex-col gap-1 mb-0 text-xs list-disc ltr:pl-4 rtl:pr-4 text-n-slate-11"
          >
            <li>
              {{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.INSIDE_WINDOW') }}
            </li>
            <li>
              {{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.OUTSIDE_WINDOW') }}
            </li>
            <li>{{ $t('CARDS.FORMS.ACKNOWLEDGEMENT_TEMPLATE.CREATION') }}</li>
          </ul>
        </section>

        <Button
          type="submit"
          class="self-start"
          :label="$t('CARDS.FORMS.SAVE')"
          :is-loading="isSaving"
          :disabled="!canSave"
          data-testid="form-save"
        />
      </form>
    </template>
  </SettingsLayout>
</template>
